import React from 'react';
import './FolderLabelingStatusDock.css';

export type FolderLabelingBanner =
  | { kind: 'success'; text: string }
  | { kind: 'error'; text: string }
  | { kind: 'partial'; text: string; failedFiles: File[] };

export interface FolderLabelingStatusDockProps {
  /** Show dock while labeling or when a result banner is visible */
  visible: boolean;
  running: boolean;
  runningMessage: string;
  banner: FolderLabelingBanner | null;
  onDismissBanner: () => void;
  /** Manual labeling for failed / errored imports */
  onManualProcess: (() => void) | null;
}

const FolderLabelingStatusDock: React.FC<FolderLabelingStatusDockProps> = ({
  visible,
  running,
  runningMessage,
  banner,
  onDismissBanner,
  onManualProcess,
}) => {
  if (!visible) return null;

  if (running) {
    return (
      <aside
        className="folder-labeling-dock folder-labeling-dock--running"
        role="status"
        aria-live="polite"
        aria-busy="true"
      >
        <div className="folder-labeling-dock-inner">
          <div className="folder-labeling-dock-icon" aria-hidden>
            <i className="fas fa-spinner fa-spin" />
          </div>
          <div className="folder-labeling-dock-body">
            <p className="folder-labeling-dock-title">Labeling photos</p>
            <p className="folder-labeling-dock-text">{runningMessage || 'Working…'}</p>
          </div>
        </div>
      </aside>
    );
  }

  if (!banner) return null;

  const variant =
    banner.kind === 'success' ? 'success' : banner.kind === 'error' ? 'error' : 'partial';
  const title =
    banner.kind === 'success'
      ? 'Done'
      : banner.kind === 'error'
        ? 'Labeling failed'
        : 'Needs attention';

  const iconClass =
    banner.kind === 'success' ? 'fas fa-check' : banner.kind === 'error' ? 'fas fa-exclamation-triangle' : 'fas fa-exclamation-circle';

  return (
    <aside
      className={`folder-labeling-dock folder-labeling-dock--${variant}`}
      role="status"
      aria-live="polite"
    >
      <div className="folder-labeling-dock-inner">
        <div className="folder-labeling-dock-icon" aria-hidden>
          <i className={iconClass} />
        </div>
        <div className="folder-labeling-dock-body">
          <p className="folder-labeling-dock-title">{title}</p>
          <p className="folder-labeling-dock-text">{banner.text}</p>
          {(banner.kind === 'error' || banner.kind === 'partial') && onManualProcess && (
            <div className="folder-labeling-dock-actions">
              <button type="button" className="folder-labeling-dock-btn folder-labeling-dock-btn--primary" onClick={onManualProcess}>
                <i className="fas fa-hand-pointer" aria-hidden />
                Assign areas manually
              </button>
            </div>
          )}
        </div>
        <button type="button" className="folder-labeling-dock-dismiss" onClick={onDismissBanner} aria-label="Dismiss">
          <i className="fas fa-times" aria-hidden />
        </button>
      </div>
    </aside>
  );
};

export default FolderLabelingStatusDock;
